import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { Cacheable, CacheBuster } from 'ts-cacheable';
import { MetricList } from 'src/app/metric/model/metricList';
import { MetricEdit } from 'src/app/metric/model/metricEdit';
import { MessengerService } from './messenger.service';
import { TranslateService } from '@ngx-translate/core';

const metricsCacheBuster = new Subject<void>();

@Injectable({
  providedIn: 'root'
})
export class MetricService implements OnDestroy
{
  public metricTypes = [
    { name: 'Rate Based', value: 'RATE_BASED' },
    { name: 'Count Based', value: 'COUNT_BASED' }
  ];

  public metricRateModes = [
    { name: 'Calculated', value: 'CALCULATED' },
    { name: 'Manual', value: 'MANUAL' }
  ];

  private messagingLanguageChangedSubscription: Subscription;

  constructor(
    private http: HttpClient,
    private messengerService: MessengerService,
    private translate: TranslateService)
  {
    this.loadLocalizations();
    this.messagingLanguageChangedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'languageChanged')
        {
          this.loadLocalizations();
        }
      }
    });
  }

  /**
   * Get full Metrics object.
   */
  @Cacheable(
    {
      cacheBusterObserver: metricsCacheBuster
    })
  public getCachedMetrics(activeOnly = false): Observable<MetricList[]>
  {
    return this.http.get<MetricList[]>('/metrics?activeonly=' + activeOnly);
  }

  /**
   * Delete a Metric.
   *
   * @param id Metric id
   */
  @CacheBuster(
    {
      cacheBusterNotifier: metricsCacheBuster
    })
  public deleteMetric(id: number): Observable<MetricList>
  {
    return this.http.delete<MetricList>(`/metric/${id}`);
  }

  /**
   * Save a Metric.
   *
   * @param data Metric to persit
   * @param isNew Indicate if this is a new or update
   */
  @CacheBuster(
    {
      cacheBusterNotifier: metricsCacheBuster
    })
  public saveMetric(data: any, isNew: boolean, metricId?: number): Observable<any>
  {
    if (isNew)
    {
      return this.http.post('/metric', data);
    }
    else
    {
      return this.http.put(`/metric/${metricId}`, data);
    }
  }

  public ngOnDestroy(): void
  {
    if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
  }

  /**
   * Get Metric for grid list.
   */
  public getMetricList(): Observable<MetricList[]>
  {
    return this.http.get<MetricList[]>('/metrics/list');
  }

  /**
   * Get full Metrics object.
   */
  public getMetrics(activeOnly = false): Observable<MetricList[]>
  {
    return this.http.get<MetricList[]>(`/metrics?activeonly=${activeOnly}`);
  }

  /**
   * Get Metric by id.
   *
   * @param id Metric id
   */
  public getMetricById(id: number): Observable<MetricEdit>
  {
    return this.http.get<MetricEdit>(`/metric/${id}`);
  }

  /**
   * Check if Metric is in use
   *
   * @param id Metric Id
   */
  public existsMetric(id: number): Observable<any>
  {
    return this.http.get(`/metric/inuse/${id}`);
  }

  /**
   * Check if Metric Name exists to avoid name duplicates
   *
   * @param name Metric Name
   */
  public existsMetricName(name: string): Observable<any>
  {
    return this.http.get(`/metric/exists?name=${name}`);
  }

  /**
   * Load localizations
   */
  private loadLocalizations(): void
  {
    this.translate.get(['METRIC-TYPE-RATE', 'METRIC-TYPE-COUNT']).subscribe({
      next: (res) =>
      {
        let index = 0;
        for (const key in res)
        {
          if ({}.hasOwnProperty.call(res, key))
          {
            this.metricTypes[index].name = res[key];
            index++;
          }
        }
      }
    });

    this.translate.get(['RATE-MODE-CALCULATED', 'RATE-MODE-MANUAL']).subscribe({
      next: (res) =>
      {
        let index = 0;
        for (const key in res)
        {
          if ({}.hasOwnProperty.call(res, key))
          {
            this.metricRateModes[index].name = res[key];
            index++;
          }
        }
      }
    });
  }
}
